import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { BookOpen, ChevronLeft, Download, MessageSquare, Share2, Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Skeleton } from "../../components/ui/skeleton";
import { api } from "../../lib/api";
import { formatRelative } from "../../lib/utils";
import type { NotebookItem } from "../../types/api";
import type { LivingDocItem } from "../living-doc/LivingDocBlock";
import { LivingDocBlocks } from "../living-doc/LivingDocBlocks";

const STYLES = [
  { id: "notes", label: "Notes" },
  { id: "runbook", label: "Runbook" },
  { id: "guide", label: "Guide" },
  { id: "checklist", label: "Checklist" },
  { id: "architecture", label: "Architecture" },
  { id: "onboarding", label: "Onboarding" },
  { id: "executive_brief", label: "Executive brief" },
  { id: "troubleshooting", label: "Troubleshooting" },
];

/**
 * B2.2 — Notebook detail as a living document.
 *
 * Layout: doc-style header (title, meta, actions) + LivingDoc blocks centre.
 * No more left aside picker — every item is rendered inline as a block,
 * editable, draggable, removable. Add-here dividers between items.
 */
export function NotebookDetail() {
  const { notebookId } = useParams({ strict: false }) as { notebookId: string };
  const qc = useQueryClient();
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTitle, setComposeTitle] = useState("");
  const [composeStyle, setComposeStyle] = useState("notes");

  const detail = useQuery({
    queryKey: ["notebook", notebookId],
    queryFn: () => api.getNotebook(notebookId),
    staleTime: 30_000,
  });

  const updateItem = useMutation({
    mutationFn: ({ itemId, text }: { itemId: string; text: string }) =>
      api.patchNotebookItem(notebookId, itemId, { note_text: text }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notebook", notebookId] });
    },
    onError: (err) => toast.error(`No se pudo guardar: ${String(err)}`),
  });

  const removeItem = useMutation({
    mutationFn: (itemId: string) => api.removeNotebookItem(notebookId, itemId),
    onMutate: async (itemId) => {
      await qc.cancelQueries({ queryKey: ["notebook", notebookId] });
      const prev = qc.getQueryData<typeof detail.data>(["notebook", notebookId]);
      qc.setQueryData<typeof detail.data>(["notebook", notebookId], (current) =>
        current
          ? { ...current, items: (current.items ?? []).filter((i) => i.id !== itemId) }
          : current,
      );
      return { prev };
    },
    onError: (err, _itemId, ctx) => {
      if (ctx?.prev) qc.setQueryData(["notebook", notebookId], ctx.prev);
      toast.error(`No se pudo eliminar: ${String(err)}`);
    },
    onSuccess: () => toast.success("Item eliminado"),
  });

  const reorder = useMutation({
    mutationFn: (ids: string[]) =>
      api.reorderNotebookItems(
        notebookId,
        ids.map((id, idx) => ({ id, order_index: idx })),
      ),
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: ["notebook", notebookId] });
      const prev = qc.getQueryData<typeof detail.data>(["notebook", notebookId]);
      qc.setQueryData<typeof detail.data>(["notebook", notebookId], (current) => {
        if (!current) return current;
        const byId = new Map(current.items?.map((i) => [i.id, i]));
        const items = ids
          .map((id, idx) => {
            const it = byId.get(id);
            return it ? { ...it, order_index: idx } : null;
          })
          .filter((x): x is NotebookItem => x !== null);
        return { ...current, items };
      });
      return { prev };
    },
    onError: (err, _ids, ctx) => {
      if (ctx?.prev) qc.setQueryData(["notebook", notebookId], ctx.prev);
      toast.error(`No se pudo reordenar: ${String(err)}`);
    },
  });

  const addNote = useMutation({
    mutationFn: () => api.addNotebookItem(notebookId, { item_type: "note", note_text: "" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notebook", notebookId] });
      toast.success("Nota añadida");
    },
    onError: (err) => toast.error(`No se pudo añadir: ${String(err)}`),
  });

  const compose = useMutation({
    mutationFn: () =>
      api.composeNotebook(notebookId, {
        title: composeTitle.trim(),
        style: composeStyle,
        lang: "es",
        destination: "notebook_artifact",
      }),
    onSuccess: () => {
      setComposeOpen(false);
      setComposeTitle("");
      toast.success("Composición encolada");
    },
    onError: (err) => toast.error(`No se pudo componer: ${err}`),
  });

  const items: LivingDocItem[] = (detail.data?.items ?? []).map(toLivingDocItem);

  return (
    <div className="mx-auto max-w-3xl px-6 py-6" data-testid="notebook-detail">
      <nav className="mb-4">
        <Link
          to="/notebooks"
          className="inline-flex items-center gap-1 text-xs text-(--text-tertiary) hover:text-(--text-primary)"
        >
          <ChevronLeft size={12} /> Mis apuntes
        </Link>
      </nav>

      <header className="mb-6" data-testid="notebook-header">
        <h1
          className="text-3xl font-semibold leading-tight text-(--text-primary)"
          data-testid="notebook-title"
        >
          {detail.data?.title ?? <Skeleton className="h-8 w-2/3" />}
        </h1>
        {detail.data?.description && (
          <p className="mt-2 text-sm text-(--text-secondary)">{detail.data.description}</p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-(--text-tertiary)">
          {detail.data && (
            <>
              <Badge variant="default" className="text-[10px]">
                {detail.data.scope}
              </Badge>
              <Badge variant="muted" className="text-[10px]">
                {detail.data.items?.length ?? 0} items
              </Badge>
              <span>actualizado {formatRelative(detail.data.updated_at)}</span>
            </>
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="sm"
            data-testid="notebook-action-add-note"
            onClick={() => addNote.mutate()}
            disabled={addNote.isPending}
          >
            <BookOpen size={13} /> Nueva nota
          </Button>
          <Button variant="secondary" size="sm" asChild>
            <Link to="/chat">
              <MessageSquare size={13} /> Chat scoped
            </Link>
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setComposeOpen(true)}
            data-testid="notebook-action-compose"
          >
            <Sparkles size={13} /> Componer
          </Button>
          <Button variant="secondary" size="sm">
            <Share2 size={13} /> Compartir
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => toast.message("Exportar Markdown — disponible tras B4")}
          >
            <Download size={13} /> Exportar
          </Button>
        </div>
      </header>

      {detail.isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      )}

      {detail.data && (
        <LivingDocBlocks
          items={items}
          onUpdateNote={(id, text) => updateItem.mutate({ itemId: id, text })}
          onRemove={(id) => removeItem.mutate(id)}
          onReorder={(ids) => reorder.mutate(ids)}
          onInsertAt={(_index) => addNote.mutate()}
        />
      )}

      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Componer desde notebook</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-(--text-tertiary)" htmlFor="compose-title">
                Título
              </label>
              <Input
                id="compose-title"
                placeholder="NetBox onboarding — guía consolidada"
                value={composeTitle}
                onChange={(e) => setComposeTitle(e.target.value)}
                autoFocus
              />
            </div>
            <fieldset>
              <legend className="text-xs text-(--text-tertiary)">Estilo</legend>
              <div className="mt-1 grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                {STYLES.map((s) => (
                  <button
                    type="button"
                    key={s.id}
                    onClick={() => setComposeStyle(s.id)}
                    className={`rounded-md border px-2 py-1.5 text-xs transition ${
                      composeStyle === s.id
                        ? "border-(--accent-500) bg-(--accent-50) text-(--accent-700)"
                        : "border-(--border-subtle) hover:bg-(--bg-hover)"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </fieldset>
            <p className="text-[10px] text-(--text-tertiary)">
              Coste estimado: ~$0.04 · ~1500 tokens
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setComposeOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              disabled={!composeTitle.trim() || compose.isPending}
              onClick={() => compose.mutate()}
            >
              <Sparkles size={13} /> Componer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function toLivingDocItem(it: NotebookItem): LivingDocItem {
  return {
    id: it.id,
    item_type: it.item_type,
    title: it.title,
    selected_text: it.selected_text,
    note_text: it.note_text,
    source_doc_id: it.source_doc_id,
    source_section_id: it.source_section_id,
    source_title: it.source_doc_id ? `Doc ${it.source_doc_id.slice(0, 8)}` : null,
    source_version: null,
    source_path: null,
    created_at: it.created_at,
  };
}
